// Dragging work onto another machine, as pure arithmetic (#6771, epic #6552; prototype
// pointerdown `:2770-2782`, startDrag / tick / hit `:2806-2862`, finish() `:2887-2916`):
// who may press where, the 7 px arm, Esc, the auto-scroll step, the ghost's width and its
// motion, and where the drop panel sits. `components/machine-card/DragLayer.tsx` feeds it
// pointer events and paints what it answers; nothing here reads the DOM, a signal or the
// clock.
//
// REDUCED MOTION IS A STILL FRAME (owner ruling 2026-10-20): the ghost tracks the pointer
// with no easing, tilt or lift; the caller skips the fly-back, the shakes and the pick flash
// (`./reduced-motion.ts`, the one reader of the query).

/** A press becomes a drag once it travels this far, `|dx| + |dy|`; shorter stays a click. */
export const DRAG_THRESHOLD_PX = 7;
/** The ghost's width: the row's, clamped. */
export const GHOST_MIN_PX = 250;
export const GHOST_MAX_PX = 320;
/** Within this far of the viewport's top or bottom edge the page scrolls. */
export const EDGE_PX = 72;
/** Esc, a refused drop or a refused move: the ghost flies back to its row. */
export const FLY_BACK_MS = 440;
export const FLY_BACK_EASING = 'cubic-bezier(0.34,1.4,0.64,1)';
/** A 201: the ghost settles onto the target's Now or Mission zone, fading from 78%. */
export const SETTLE_MS = 480;
export const SETTLE_EASING = 'cubic-bezier(0.16,1,0.3,1)';
/** A source refusal shakes the row; a refused drop shakes the card, outlined red a little longer. */
export const ROW_SHAKE_MS = 320;
export const CARD_SHAKE_MS = 380;
export const REJECT_OUTLINE_MS = 420;
/** A click right after a drop is swallowed for this long. */
export const CLICK_SWALLOW_MS = 80;
/** A Move to pick flashes this long before the move is sent. */
export const PICK_FLASH_MS = 260;

/** Where a press landed, as the row's pointerdown sees it. */
export type PressTarget = {
  /** `PointerEvent.pointerType`: `mouse`, `touch` or `pen`. */
  readonly pointerType: string;
  readonly button: number;
  /** On the row's grip. */
  readonly onGrip: boolean;
  /** The row's move control is disabled (the move limit, or the page's lock). */
  readonly gripDisabled: boolean;
  /** On a button (other than the row's own toggle), a field, a link, a menu or details. */
  readonly onControl: boolean;
  /** The row has a grip at all: only a movable row can be dragged. */
  readonly hasGrip: boolean;
};

/**
 * Whether a press may arm a drag: primary button only; a mouse from the row or its header
 * (never its controls), touch and pen from the grip only; never while the row's move control
 * is disabled, wherever on the row the press lands.
 */
export function mayPress(p: PressTarget): boolean {
  if (p.button !== 0 || !p.hasGrip || p.gripDisabled) return false;
  if (p.onGrip) return true;
  return p.pointerType === 'mouse' && !p.onControl;
}

export type DragState =
  | { readonly phase: 'idle' }
  | {
      readonly phase: 'pressed';
      readonly pointerId: number;
      readonly x0: number;
      readonly y0: number;
    }
  | {
      readonly phase: 'dragging';
      readonly pointerId: number;
      readonly x0: number;
      readonly y0: number;
      readonly x: number;
      readonly y: number;
    };

export type DragEvent =
  | { readonly type: 'down'; readonly pointerId: number; readonly x: number; readonly y: number }
  | { readonly type: 'move'; readonly pointerId: number; readonly x: number; readonly y: number }
  | { readonly type: 'up'; readonly pointerId: number }
  | { readonly type: 'cancel'; readonly pointerId: number }
  | { readonly type: 'escape' };

/**
 * What the step asks the caller to do: `start` the drag (ask the preview), `drop` on what is
 * under the pointer, `fly_back` (Esc or a cancelled pointer), or let the press be a `click`.
 */
export type DragEffect = 'none' | 'start' | 'drop' | 'fly_back' | 'click';

export const IDLE: DragState = { phase: 'idle' };

/** One step of the press-then-drag machine. A pointer that is not the pressing one moves nothing. */
export function dragStep(
  s: DragState,
  e: DragEvent,
): { readonly state: DragState; readonly effect: DragEffect } {
  const same = (st: DragState): boolean =>
    e.type === 'escape' ||
    (st.phase !== 'idle' && 'pointerId' in e && e.pointerId === st.pointerId);
  if (s.phase === 'idle') {
    if (e.type !== 'down') return { state: s, effect: 'none' };
    return {
      state: { phase: 'pressed', pointerId: e.pointerId, x0: e.x, y0: e.y },
      effect: 'none',
    };
  }
  if (!same(s)) return { state: s, effect: 'none' };
  if (s.phase === 'pressed') {
    if (e.type === 'move') {
      if (Math.abs(e.x - s.x0) + Math.abs(e.y - s.y0) < DRAG_THRESHOLD_PX)
        return { state: s, effect: 'none' };
      return { state: { ...s, phase: 'dragging', x: e.x, y: e.y }, effect: 'start' };
    }
    if (e.type === 'up') return { state: IDLE, effect: 'click' };
    // Esc or a cancelled pointer ends the press: nothing started, so nothing flies back.
    if (e.type === 'cancel' || e.type === 'escape') return { state: IDLE, effect: 'none' };
    return { state: s, effect: 'none' };
  }
  switch (e.type) {
    case 'move':
      return { state: { ...s, x: e.x, y: e.y }, effect: 'none' };
    case 'up':
      return { state: IDLE, effect: 'drop' };
    case 'cancel':
    case 'escape':
      return { state: IDLE, effect: 'fly_back' };
    case 'down':
      return { state: s, effect: 'none' };
  }
}

/** Pixels to scroll this frame: `ceil(distance / 5)` within {@link EDGE_PX} of an edge, else 0. */
export function autoScrollStep(y: number, viewportHeight: number): number {
  if (y < EDGE_PX) return -Math.ceil((EDGE_PX - y) / 5);
  if (y > viewportHeight - EDGE_PX) return Math.ceil((y - viewportHeight + EDGE_PX) / 5);
  return 0;
}

/** The ghost's width for a row of `rowWidth` px. */
export function ghostWidth(rowWidth: number): number {
  return Math.round(Math.min(GHOST_MAX_PX, Math.max(GHOST_MIN_PX, rowWidth)));
}

/** The ghost between frames: its position, tilt, pointer speed, last x and lift progress. */
export type GhostMotion = {
  readonly gx: number;
  readonly gy: number;
  readonly rot: number;
  readonly vx: number;
  readonly lx: number;
  /** 0..1 over ten frames: the lift's progress. */
  readonly t: number;
  readonly scale: number;
};

export const REST_MOTION: GhostMotion = { gx: 0, gy: 0, rot: 0, vx: 0, lx: 0, t: 0, scale: 1 };

/** The ghost's maximum tilt, in degrees, and its lift. */
const MAX_TILT = 6;
const LIFT = 0.035;

/**
 * One frame of the ghost toward `at` (the pointer less the grab offset): eases 0.3 a frame,
 * tilts up to ±6° with pointer speed (velocity × 0.6, eased 0.25) and lifts to 1.035 over ten
 * frames, ease-out cubic. Under reduced motion it sits on `at`, flat and unscaled.
 */
export function ghostMotion(
  m: GhostMotion,
  at: { readonly x: number; readonly y: number },
  reduced: boolean,
): GhostMotion {
  const k = reduced ? 1 : 0.3;
  const gx = m.gx + (at.x - m.gx) * k;
  const gy = m.gy + (at.y - m.gy) * k;
  const vx = m.vx * 0.8 + (at.x - m.lx) * 0.2;
  const tilt = reduced ? 0 : Math.max(-MAX_TILT, Math.min(MAX_TILT, vx * 0.6));
  const rot = reduced ? 0 : m.rot + (tilt - m.rot) * 0.25;
  const t = Math.min(1, m.t + 0.1);
  const scale = reduced ? 1 : 1 + LIFT * (1 - (1 - t) ** 3);
  return { gx, gy, rot, vx, lx: at.x, t, scale };
}

/** The ghost's CSS transform for a motion frame. */
export function ghostTransform(m: GhostMotion): string {
  return `translate3d(${m.gx.toFixed(1)}px,${m.gy.toFixed(1)}px,0) rotate(${m.rot.toFixed(2)}deg) scale(${m.scale.toFixed(3)})`;
}

/**
 * The drop panel's top inside the hovered card, from the pointer's offset in it: above the
 * pointer when 12 px or more is left, else 70 px below; never closer than 12 px to the edge.
 */
export function panelTop(pointerY: number, cardHeight: number, panelHeight: number): number {
  const above = pointerY - panelHeight - 34;
  const top = above >= 12 ? above : Math.min(cardHeight - panelHeight - 12, pointerY + 70);
  return Math.max(12, Math.round(top));
}
